/**
 * Floating AI Recovery Coach chat launcher.
 *
 * Three states, controlled by the patient:
 *   • closed    — small circular button in the bottom-right corner
 *   • minimized — compact header bar (chat hidden but still docked)
 *   • open      — full chat panel (default when first clicked)
 *
 * Rendered inside DashLayout only when the current user is a patient with
 * an active membership (the coach access gate). Mounted once globally so
 * conversation state survives page-to-page navigation on the patient side.
 */
import { useEffect, useRef, useState } from "react";
import { scrollBoxToBottom } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ChevronDown, Lock, MessageCircle, Send, Sparkles, X } from "lucide-react";

const HEADERS = () => ({ Authorization: `Bearer ${localStorage.getItem("fl_token") || ""}` });
const STORAGE_KEY = "fl_coach_widget_state";   // { view: 'closed'|'min'|'open', day: 'YYYY-MM-DD' }

function todayKey() {
  const d = new Date();
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function readInitialView() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return "closed";
    const parsed = JSON.parse(raw);
    // If the persisted state is from a previous day, force the widget back to closed
    // so the patient starts fresh in a new day.
    if (parsed?.day && parsed.day !== todayKey()) return "closed";
    return ["closed", "min", "open"].includes(parsed?.view) ? parsed.view : "closed";
  } catch { return "closed"; }
}

function persistView(v) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ view: v, day: todayKey() })); } catch {}
}

export default function CoachChatWidget() {
  const { user } = useAuth();
  const loc = useLocation();
  const [access, setAccess] = useState({ loaded: false, allowed: false });
  const [view, setView] = useState(readInitialView);        // 'closed'|'min'|'open'
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionDay, setSessionDay] = useState(todayKey()); // for new-day watchdog
  const endRef = useRef(null);
  const widgetScrollBox = useRef(null);
  const inputRef = useRef(null);

  const isPatient = user?.role === "patient";
  const onPatientRoute = loc.pathname.startsWith("/app");
  // Hide on the dedicated coach page — that view already has an inline chat.
  const onCoachPage = loc.pathname.startsWith("/app/coach");
  const shouldMount = isPatient && onPatientRoute && !onCoachPage;

  // Membership gate (only relevant once we know we should mount at all)
  useEffect(() => {
    if (!shouldMount) return;
    axios.get(`${API}/coach/access`, { headers: HEADERS() })
      .then((r) => setAccess({ loaded: true, allowed: !!r.data?.allowed }))
      .catch(() => setAccess({ loaded: true, allowed: false }));
  }, [shouldMount]);

  // Reset conversation view every time the widget is (re)opened.
  // We intentionally do NOT load history — the server keeps the full transcript
  // for clinician monitoring, but the patient sees a clean slate each session.
  useEffect(() => {
    if (view === "open") {
      setMsgs([]);
      setInput("");
      setSessionDay(todayKey());
    }
  }, [view]);

  // New-day watchdog: if the widget stays open across midnight, force-close so
  // the next interaction starts a fresh conversation.
  useEffect(() => {
    if (view !== "open") return;
    const iv = setInterval(() => {
      if (todayKey() !== sessionDay) setView("closed");
    }, 60_000);
    return () => clearInterval(iv);
  }, [view, sessionDay]);

  useEffect(() => { persistView(view); }, [view]);

  useEffect(() => {
    if (view === "open") {
      scrollBoxToBottom(widgetScrollBox.current, true);
      // Delay focus so the entrance transition can finish first
      const t = setTimeout(() => inputRef.current?.focus(), 220);
      return () => clearTimeout(t);
    }
  }, [view, msgs.length]);

  if (!shouldMount) return null;

  const first = (user?.name || "").split(" ")[0] || "there";

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy) return;
    setMsgs((s) => [...s, { role: "user", content: text, id: "tmp-" + Date.now() }]);
    setInput("");
    setBusy(true);
    try {
      const res = await axios.post(`${API}/coach/chat`, { message: text }, { headers: HEADERS() });
      setMsgs((s) => [...s, { role: "assistant", content: res.data.reply, id: "resp-" + Date.now() }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Coach unavailable. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // ---------- Closed: floating launcher button ----------
  if (view === "closed") {
    return (
      <button
        onClick={() => setView("open")}
        data-testid="coach-widget-open-btn"
        aria-label="Open AI Recovery Coach"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center transition-transform duration-200 hover:scale-105 active:scale-95"
        style={{
          background: "var(--fl-orange)",
          color: "#fff",
          boxShadow: "0 12px 32px -8px rgba(255,69,0,.45), 0 0 0 4px color-mix(in oklab, var(--fl-orange) 18%, transparent)",
        }}
      >
        <Sparkles size={22} />
      </button>
    );
  }

  // ---------- Minimized: docked header bar ----------
  if (view === "min") {
    return (
      <div
        className="fixed bottom-6 right-6 z-40 rounded-full pl-4 pr-2 py-2 flex items-center gap-3 shadow-xl cursor-pointer group"
        style={{
          background: "var(--fl-bg-2)",
          border: "1px solid color-mix(in oklab, var(--fl-orange) 45%, var(--fl-border))",
        }}
        onClick={() => setView("open")}
        data-testid="coach-widget-minimized"
      >
        <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "color-mix(in oklab, var(--fl-orange) 22%, var(--fl-bg-2))", color: "var(--fl-orange)" }}>
          <MessageCircle size={16} />
        </span>
        <span className="text-sm font-medium" style={{ color: "var(--fl-text)" }}>
          Recovery Coach
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setView("closed"); }}
          data-testid="coach-widget-close-btn-min"
          aria-label="Close AI Recovery Coach"
          className="ml-1 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
          style={{ color: "var(--fl-muted)" }}
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // ---------- Open: full panel ----------
  const gated = access.loaded && !access.allowed;

  return (
    <div
      className="fixed bottom-6 right-6 z-40 flex flex-col rounded-lg overflow-hidden shadow-2xl"
      style={{
        width: "min(380px, calc(100vw - 2rem))",
        height: "min(560px, calc(100vh - 5rem))",
        background: "var(--fl-bg)",
        border: "1px solid var(--fl-border)",
        boxShadow: "0 24px 64px -16px rgba(0,0,0,.55)",
      }}
      data-testid="coach-widget-open"
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0"
        style={{ borderColor: "var(--fl-border)", background: "var(--fl-bg-2)" }}
      >
        <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "color-mix(in oklab, var(--fl-orange) 22%, var(--fl-bg-2))", color: "var(--fl-orange)" }}>
          <Sparkles size={16} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="fl-heading text-sm">AI Recovery Coach</div>
          <div className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>Available 24/7 · Powered by Claude</div>
        </div>
        <button
          onClick={() => setView("min")}
          data-testid="coach-widget-minimize-btn"
          aria-label="Minimize"
          className="w-7 h-7 rounded-md flex items-center justify-center transition-colors hover:bg-white/5"
          style={{ color: "var(--fl-muted)" }}
        >
          <ChevronDown size={16} />
        </button>
        <button
          onClick={() => setView("closed")}
          data-testid="coach-widget-close-btn"
          aria-label="Close"
          className="w-7 h-7 rounded-md flex items-center justify-center transition-colors hover:bg-white/5"
          style={{ color: "var(--fl-muted)" }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      {gated ? (
        <div className="flex-1 p-5 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
               style={{ background: "color-mix(in oklab, var(--fl-orange) 18%, var(--fl-bg-2))", color: "var(--fl-orange)" }}>
            <Lock size={22} />
          </div>
          <h3 className="fl-heading text-lg">Members-only feature</h3>
          <p className="text-xs mt-2 max-w-[260px]" style={{ color: "var(--fl-muted)" }}>
            Upgrade to a Functional LAB membership to chat 24/7 with your Recovery Coach and see your live Recovery Score.
          </p>
          <Link
            to="/app/packages"
            onClick={() => setView("closed")}
            className="fl-btn text-xs mt-4"
            data-testid="coach-widget-upgrade-cta"
          >
            See memberships
          </Link>
        </div>
      ) : (
        <>
          <div
            ref={widgetScrollBox}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
            data-testid="coach-widget-messages"
          >
            {msgs.length === 0 && (
              <div
                className="text-sm p-3 rounded-md"
                style={{ background: "var(--fl-bg-2)", color: "var(--fl-muted)" }}
              >
                Hi {first} — I&apos;m your Recovery Coach. Ask me anything about your programme,
                how you&apos;re feeling, or how to progress. If something feels off, tell me and I&apos;ll
                flag it for your clinician.
              </div>
            )}
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className="max-w-[85%] px-3 py-2 rounded-lg text-sm leading-relaxed whitespace-pre-wrap"
                  style={{
                    background: m.role === "user" ? "var(--fl-orange)" : "var(--fl-bg-2)",
                    color: m.role === "user" ? "#fff" : "var(--fl-text)",
                  }}
                  data-testid={`coach-widget-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>
                Coach is thinking…
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form
            onSubmit={send}
            className="p-3 border-t flex gap-2 flex-shrink-0"
            style={{ borderColor: "var(--fl-border)" }}
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Message your Recovery Coach…"
              className="fl-input flex-1 text-sm px-3 py-2 rounded-md"
              disabled={busy}
              data-testid="coach-widget-input"
            />
            <button type="submit" disabled={busy || !input.trim()} className="fl-btn text-sm" data-testid="coach-widget-send">
              <Send size={14} />
            </button>
          </form>
        </>
      )}
    </div>
  );
}
